<template>
  <div v-if="open" class="vm-detail-panel-card">
    <div class="vm-detail-panel-header">
      <div>
        <p class="vm-detail-panel-eyebrow">Virtual Machine</p>
        <h3 class="vm-detail-panel-title">{{ title }}</h3>
      </div>
      <button type="button" class="vm-detail-close-button" aria-label="Close" @click="closePanel">
        <i class="fa fa-times" aria-hidden="true"></i>
      </button>
    </div>
    <div class="vm-detail-panel-body" v-if="detailData">
      <table class="table table-bordered table-condensed vm-detail-table">
        <tbody>
          <tr v-for="(row, index) in detailData.rows" :key="`row-${index}`">
            <th><div class="text-center">{{ row.label }}</div></th>
            <td>
              <div class="text-center">
                <template v-if="row.type === 'link' && row.url">
                  <a :href="row.url">{{ row.value }}</a>
                </template>
                <template v-else>
                  {{ row.value }}
                </template>
              </div>
            </td>
          </tr>

          <template v-for="(nic, nicIndex) in detailData.nics || []" :key="`nic-${nicIndex}`">
            <tr class="vm-detail-subsection-heading">
              <td colspan="2">
                <span class="vm-detail-subsection-chip">{{ nic.title }}</span>
              </td>
            </tr>
            <tr
              v-for="(row, rowIndex) in nic.rows"
              :key="`nic-${nicIndex}-row-${rowIndex}`"
              class="vm-detail-subsection-row"
            >
              <th><div class="text-center vm-detail-subsection-label">{{ row.label }}</div></th>
              <td>
                <div class="text-center">
                  <template v-if="row.type === 'ip_addresses'">
                    <template v-if="row.ipv4 && row.ipv4.length">
                      <div><strong>IPv4</strong></div>
                      <div v-for="(ip, ipIndex) in row.ipv4" :key="`nic-${nicIndex}-ipv4-${ipIndex}`">{{ ip }}</div>
                    </template>

                    <template v-if="row.ipv6 && row.ipv6.length">
                      <div v-if="row.ipv4 && row.ipv4.length" class="vm-detail-address-divider"></div>
                      <div><strong>IPv6</strong></div>
                      <div v-for="(ip, ipIndex) in row.ipv6" :key="`nic-${nicIndex}-ipv6-${ipIndex}`">{{ ip }}</div>
                    </template>

                    <template v-if="(!row.ipv4 || !row.ipv4.length) && (!row.ipv6 || !row.ipv6.length)">
                      N/A
                    </template>
                  </template>
                  <template v-else>
                    {{ row.value }}
                  </template>
                </div>
              </td>
            </tr>
          </template>

          <tr v-for="(row, index) in detailData.footer_rows || []" :key="`footer-${index}`">
            <th><div class="text-center">{{ row.label }}</div></th>
            <td>
              <div class="text-center">
                <template v-if="row.type === 'secret'">
                  <div class="text-center vm-secret-field" :data-secret-value="row.value">
                    <span class="vm-secret-value">{{ revealedSecrets[row.label] ? row.value : '••••••••' }}</span>
                    <span class="vm-secret-actions">
                      <button type="button" class="vm-secret-action" @click="toggleSecret(row.label)">
                        {{ revealedSecrets[row.label] ? 'Hide' : 'Show' }}
                      </button>
                      <button type="button" class="vm-secret-action" @click="copySecret(row.label, row.value)">
                        {{ copiedSecrets[row.label] ? 'Copied' : 'Copy' }}
                      </button>
                    </span>
                  </div>
                </template>
                <template v-else>
                  <span class="vm-detail-prewrap">{{ row.value }}</span>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  shellId: {
    type: String,
    default: 'vm-detail-shell'
  }
})

const open = ref(false)
const title = ref('')
const detailData = ref(null)
const revealedSecrets = ref({})
const copiedSecrets = ref({})
const panelId = `${props.shellId}-panel`

function handlePanelUpdate(event) {
  const detail = event.detail || {}

  title.value = detail.title || ''
  detailData.value = detail.detailData || null
  revealedSecrets.value = {}
  copiedSecrets.value = {}
  open.value = true
}

function handlePanelClear() {
  open.value = false
  title.value = ''
  detailData.value = null
  revealedSecrets.value = {}
  copiedSecrets.value = {}
}

function closePanel() {
  const shell = document.getElementById(props.shellId)
  const panel = document.getElementById(panelId)

  handlePanelClear()

  if (panel) {
    panel.setAttribute('aria-hidden', 'true')
  }

  if (shell) {
    shell.classList.remove('vm-detail-shell-open')
  }

  window.sccVmDetailUrl = null
  window.sccVmDetailVmId = null
  window.sccVmDetailShellId = null
  window.sccVmDetailMode = null
}

function toggleSecret(label) {
  revealedSecrets.value = {
    ...revealedSecrets.value,
    [label]: !revealedSecrets.value[label]
  }
}

function copySecret(label, value) {
  if (!navigator.clipboard || !value) {
    return
  }

  navigator.clipboard.writeText(value).then(() => {
    copiedSecrets.value = {
      ...copiedSecrets.value,
      [label]: true
    }

    window.setTimeout(() => {
      copiedSecrets.value = {
        ...copiedSecrets.value,
        [label]: false
      }
    }, 1200)
  })
}

onMounted(() => {
  const panel = document.getElementById(panelId)

  if (!panel) {
    return
  }

  panel.addEventListener('scc:vm-detail:update', handlePanelUpdate)
  panel.addEventListener('scc:vm-detail:clear', handlePanelClear)
})

onBeforeUnmount(() => {
  const panel = document.getElementById(panelId)

  if (!panel) {
    return
  }

  panel.removeEventListener('scc:vm-detail:update', handlePanelUpdate)
  panel.removeEventListener('scc:vm-detail:clear', handlePanelClear)
})
</script>

<style scoped>
.vm-detail-prewrap {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
